/**
 * PRODUCT WEIGHT — a real weight when one exists, derived when it does not.
 *
 * START AT `resolveUnitWeightPounds`, at the foot of this file: it is the
 * ladder every consumer should read (stated weight -> derived formula ->
 * unknown), and its own block explains how a stated weight is screened before
 * it is believed. The formula documented below is rung two — the fallback,
 * not the answer.
 *
 * Neither the item master nor Walmart's product pages publish a weight for
 * laundry consumables — they list volume or count — and the PSA Product
 * weight field carries the placeholder 1 in every WALMART_LAUNDRY psa. The
 * weight is therefore computed from what IS known: the pack magnitude and
 * the pack format. It matters on the shelf: heavy jugs belong low, where a
 * shopper can lift them, so BULK is the tag a planogrammer merchandises
 * against.
 *
 * MEANT TO GO DORMANT, not to stay permanent. `psa_product` carries a `uom`
 * beside that weight, and `product_fact.weight` exists as well; nothing reads
 * either one today, and this formula reads neither. `uom` is the single field
 * that would say whether a magnitude is fluid ounces, grams or a count — the
 * ambiguity every wrong number this formula ever produced comes from — so
 * whoever populates the master should know that a real per-SKU weight makes
 * the derivation redundant rather than wrong, and that reading `uom` is what
 * would let it widen past laundry safely.
 *
 * SCOPE — THIS IS A LAUNDRY FORMULA, and it is called on every product in
 * the catalogue. The constants below describe detergent, so a name carrying
 * no laundry evidence returns null instead of a detergent-shaped guess: dog
 * food weighed as detergent came out at 0.07x its real pack, and
 * under-reporting is the unsafe direction for a shelf load cap — the shelf
 * reads light and passes while it is overloaded. Widening the scope means
 * adding a family with its own constants, never relaxing the gate.
 *
 * MODEL NOTE: the magnitude is `size`, the workbook's "Size (fl oz / ct)"
 * column, never a count read out of the name. For a liquid it is fluid
 * ounces; for a unit-dose pack the vendor already states an ounce equivalent
 * (a 16-count tile box is 9.6, an 81-count pod tub is 48.6), so the per-piece
 * constants act as format factors on it — a count from the name would print
 * heavier numbers than the sheet. The workbook also reads "unit dose" off a
 * Pack format column a pog item does not have, which is why the name test
 * matches "Pak(s)": the ARM & HAMMER Power Paks rows.
 *
 * THREE FORMATS HAVE NO CONSTANT OF THEIR OWN. Dryer sheets, in-wash scent
 * boosters and fabric rinse are inside the gate but have no branch, so they
 * weigh through the liquid fl-oz model — which is why a 330-count box of
 * Bounce reads 14.43 lb and is tagged BULK. That is CHARACTERISED, NOT
 * ENDORSED: the liquid model is reused because it is what the pre-gate
 * formula did, so re-admitting these formats restored their existing numbers
 * instead of silently restating them. Nobody has validated that a box of
 * sheets really weighs its fl-oz equivalent. Giving them real constants is a
 * numbers-moving change and ships as one, with a quantified diff.
 *
 * Where the pack cannot be read — an unreadable magnitude, or a product
 * family this formula does not recognise — this returns null and the caller
 * shows nothing, rather than the bare 0 the sheet prints. Never coerce that
 * null to 0 inside an aggregation: a shelf that silently under-reports looks
 * safe and is not. One rule, two ways of not knowing.
 *
 * D2 (Wendy, 2026-08-24) put the formula here because it had grown four homes
 * — frontend shelf-weight surfaces, the backend pogger converter, CDT Studio's
 * weight ladder, and an unenforced twin in owa-local/generate-v12.mjs, now
 * retired. A per-SKU weight that disagrees between the screen a planner reads
 * and the optimizer that built the plan is not cosmetic.
 *
 * TWO OWNER RULINGS, recorded so nobody re-litigates them (Wendy, 2026-08-24):
 *
 *  1. UNITS ON SHELF = facings x depth-facings, WITHOUT the loose z-capped
 *     product a filler wedges into leftover depth — a fill practice, not
 *     planned stock (confirms `getUnitsOnShelf()` over `getCapacity()`, D1).
 *     Recorded here as the file every weight consumer reads first.
 *  2. WEIGHT IS PER COUNTED UNIT. `productWeightPounds` returns ONE CONSUMER
 *     UNIT; scale a case or a tray with `weightPerCountedUnit` below. Getting
 *     it backwards is a silent 12x error, hence an exported helper.
 */
import type { BaseProduct } from "@CommonTypes/merchflow/pog/product";

/** A pack at or above this weight is bulk — two hands, bottom shelves. */
export const BULK_POUNDS = 8;

const OZ_PER_POUND = 16;
/** Liquid detergent runs a little denser than water. */
const LIQUID_OZ_PER_FL_OZ = 1.08;
/** Bottle, cap and label. */
const LIQUID_PACKAGING = 1.08;
/** Ounces of detergent in one unit-dose pac, per the workbook's own factor. */
const PAC_OUNCES = 0.9;
/** Ounces in one evo tile, per the workbook's own factor. */
const TILE_OUNCES = 0.35;
/**
 * Box, film and inner liner. INHERITED FROM THE WORKBOOK, NEVER INDEPENDENTLY
 * VALIDATED — and it now carries more than it used to. Beside a format
 * constant it is a co-factor, but on the powder branch it is the WHOLE
 * multiplier: all 45 restated aisle-2 powder weights and both BULK flips are
 * `size x 1.06` and nothing else. The first question anyone asks about those
 * deltas is "is 1.06 right", and the honest answer is that the workbook says
 * so and nobody has checked.
 */
const DRY_PACKAGING = 1.06;

export type WeighableProduct = Pick<BaseProduct, "name" | "size">;

/**
 * Positive evidence that a name is a laundry consumable (see SCOPE).
 *
 * WIDEN THIS, NEVER THE FALL-THROUGH. Two whole fabric-care FORMATS were
 * missing from the first version of this gate, and the 324-record
 * characterisation set could not see it because that set is one aisle of one
 * planogram: `walmart_aisle_1`, all detergent. The second aisle of the same
 * store — `walmart_aisle_2`, 232 records, every one LAUNDRY - FABRIC CARE —
 * has dryer sheets, in-wash scent boosters and fabric rinse, and the gate
 * dropped about twenty real SKUs of them to null. Hence `dryer sheets?`,
 * `scent booster`, `in-wash` and `fabric rinse`. A laundry SKU that abstains
 * is a bug in this list, not an acceptable cost.
 *
 * FORMAT TOKENS ARE TWO WORDS WHERE ONE WOULD OVER-MATCH. `dryer sheets?`
 * (plus `dryer bar`), never bare `dryer`: a bare token reopens exactly the
 * class this gate closed, because "Conair Hair Dryer 1875W" and "Whirlpool
 * Dryer Vent Kit" are Walmart HBA and GM products that would be weighed as
 * detergent. The .psa fixtures happen to contain no hair dryers, so no
 * measurement here would have caught it. All 19 real dryer-sheet rows on
 * `walmart_aisle_2` say "Dryer Sheets", so the two-word form costs nothing.
 *
 * The same rule retired four one-word tokens that were admitting real Walmart
 * products from other aisles, three of them UNDER-reporting — the direction
 * SCOPE is about:
 *
 *   `softener` -> `fabric softener`   water-softener salt: a 40 lb bag of
 *                                     Morton pellets read 2.92 lb and missed
 *                                     the BULK tag entirely
 *   `loads?`   -> dropped             "Top Load Washer" read 0.33 lb against
 *                                     ~200 lb of appliance
 *   `oxi\w*`   -> `oxiclean|oxi`      matched "Oxide": zinc oxide ointment
 *   `stain`    -> the action forms    matched Minwax Wood Stain
 *
 * `loads?` was safe to drop outright because every laundry name carrying it
 * also carries `laundry` or `deterg` — measured, 0 rows on either aisle rely
 * on it. `\boxi\b` still takes "Oxi Boost", "Ultra Oxi" and "OxiClean"; it
 * just stops taking "Oxide". `stain` keeps its ACTION forms rather than the
 * bare word, because "Stain Remover" / "Stain Treater" / "Tough Stains" are
 * laundry and "Wood Stain" is a paint-aisle product that happens to share a
 * noun. Coverage is unchanged on both aisles: 320/320 and 227/232.
 *
 * NO BRAND TOKEN IS A CATEGORY WORD. `all`, Gain, Cheer, Era, Snuggle and
 * Xtra are out — the bare word does not mean laundry, and a real SKU says
 * what it is. The accepted cost, stated so it is not rediscovered as a bug: a
 * name giving a brand and NOTHING else — a bare "Snuggle 95oz" — abstains.
 *
 * KNOWN-ACCEPTED LEAKS, so the next reader does not rediscover them. `pods?`,
 * `tiles?`, `bleach` and `evo` are genuine laundry format words with no
 * two-word form that does not cost real rows, so they still admit K-Cup pods
 * (2.39 lb), ceramic floor tile (0.23 lb), "Bleach Free Wipes" (5.47 lb) and
 * EVO shampoo (6.96 lb). All four OVER-report, which is the forgiving
 * direction for a load cap, and that is the whole reason they are tolerable
 * where `softener` was not.
 */
const LAUNDRY_FAMILY =
  /\b(deterg\w*|laundry|fabric softener|fabric conditioner|fabric rinse|bleach|oxiclean|oxi|stain removers?|stain treater|stain fighters?|stain release|stain and odor|tough stains|in-?wash|scent boosters?|scent beads|pacs?|paks?|pods?|flings?|tiles?|evo|tide|persil|purex|woolite|dreft|downy|dryer sheets?|dryer bar)\b/i;

/**
 * Unanchored on purpose: catches "powder" and "powdered" alike. Safe because
 * POWDER SELECTS A BRANCH, IT NEVER OPENS THE GATE — "Hershey's Cocoa Powder"
 * and "Baby Powder" never reach it, because LAUNDRY_FAMILY has already
 * returned null. Anchor the gate tokens; this one is downstream of the gate.
 */
const POWDER = /powder/i;

/**
 * Anchored, both of them. Unanchored, /pac/ matched the "2-Pack" of a liquid
 * jug — 18% light, in the unsafe direction — and /evo/ matched the "Huevo" of
 * a chocolate egg. `pacs?` rather than `packs?` is deliberate: it keeps the
 * unit-dose form and leaves the generic packaging word alone.
 */
const UNIT_DOSE = /\b(pacs?|paks?|pods?|flings?)\b/i;
const TILES = /\b(tiles?|evo)\b/i;

const leadingNumber = (text: string): number | null => {
  const match = text.match(/(\d+(?:\.\d+)?)/);
  if (!match) return null;
  const value = Number(match[1]);
  return Number.isFinite(value) ? value : null;
};

/**
 * Fluid ounces for a liquid, the vendor's ounce equivalent for a unit-dose
 * pack — whichever the size column carries. Falls back to the name only
 * when size is blank.
 */
const packMagnitude = (product: WeighableProduct): number | null => {
  const size = String(product.size ?? "").trim();

  const exact = Number(size);
  if (size !== "" && Number.isFinite(exact)) {
    return exact;
  }

  const fromSize = leadingNumber(size);
  if (fromSize !== null) {
    return fromSize;
  }

  const fromName = String(product.name ?? "").match(
    /(\d+(?:\.\d+)?)\s*(?:fl\.?\s*oz|oz\b|ct\b|count\b)/i,
  );

  return fromName ? leadingNumber(fromName[1]) : null;
};

const roundPounds = (ounces: number) => +(ounces / OZ_PER_POUND).toFixed(2);

/**
 * Pounds for ONE CONSUMER UNIT of this product, or null when the pack cannot
 * be read or the product is not a laundry consumable (a caller shows nothing
 * rather than a made-up number).
 *
 * This is not the weight of a case or a tray. See `weightPerCountedUnit`.
 */
export const productWeightPounds = (product: WeighableProduct): number | null => {
  const magnitude = packMagnitude(product);
  if (magnitude === null || magnitude <= 0) {
    return null;
  }

  const name = String(product.name ?? "");

  if (!LAUNDRY_FAMILY.test(name)) {
    return null;
  }

  // THE ORDER BELOW IS LOAD-BEARING. First match wins, most specific format
  // first: TILES -> UNIT_DOSE -> POWDER -> liquid. A name carrying two format
  // words takes the earlier branch, which is correct — "Tide Powder Detergent
  // Pacs" is dosed by the piece, so UNIT_DOSE (5.96 lb at size 100) beats
  // POWDER (6.63 lb) — but it is correct by ARRANGEMENT, not by anything the
  // branches themselves say. Reordering these for tidiness silently restates
  // every two-format SKU. There is a test pinning it.
  if (TILES.test(name)) {
    return roundPounds(magnitude * TILE_OUNCES * DRY_PACKAGING);
  }

  if (UNIT_DOSE.test(name)) {
    return roundPounds(magnitude * PAC_OUNCES * DRY_PACKAGING);
  }

  // A powder’s magnitude IS its weight: 172 oz of Gain is 172 ounces of powder,
  // where 172 fl oz of Tide is a volume that still has to be weighed. Through the
  // liquid branch a powder box comes out ~8% heavy — and a powder box is exactly
  // the pack the heavy-below-four-feet rule exists for, so the error lands where
  // it matters most. The workbook normalises the powder section to ounces, so
  // `size` is the weight for every pack in it.
  if (POWDER.test(name)) {
    return roundPounds(magnitude * DRY_PACKAGING);
  }

  return roundPounds(magnitude * LIQUID_OZ_PER_FL_OZ * LIQUID_PACKAGING);
};

/**
 * Two hands, bottom shelf. Reads the WHOLE ladder (`resolveUnitWeightPounds`,
 * at the foot of this file) rather than the derived formula alone, because
 * the derivation abstains outside laundry: a stated 20 lb sack has to flag as
 * bulk even though the formula would decline to say anything about it.
 */
export const isBulkProduct = (
  product: WeighableProduct & StoredWeight,
  options: ResolveWeightOptions = {},
): boolean => {
  const pounds = unitWeightPounds(product, options);
  return pounds !== null && pounds >= BULK_POUNDS;
};

/**
 * What the caller is counting on the shelf — NOT necessarily the item's own
 * `merchandisingStyle` field. The two coincide often enough to be a trap.
 */
export type CountedUnitStyle = "UNIT" | "TRAY" | "CASE";

/**
 * Pounds for one of whatever the caller is counting (ruling 2 above).
 *
 * Weight is a `profit`-shaped quantity: a 12-pack CASE weighs
 * `unitWeight * 12`, never `unitWeight / 12`. `PoggerRequestCreationService`
 * scales `quantity` and `min_on_shelf` the other way, so an author who
 * pattern-matches on those lines lands the silent 12x error this prevents.
 *
 * PASS THE STYLE THAT DESCRIBES WHAT YOU COUNT. Frontend callers count
 * consumer units — `PlanogramItem.getUnitsOnShelf()` already multiplies by
 * `getUnits()` for every style — so they pass "UNIT".
 *
 * The two pogger paths are the trap, and they point OPPOSITE ways:
 *
 *  - FLATTENED (what `PoggerRequestCreationService` sends): every candidate
 *    goes out as `merchandising_style: UNIT`, but carrying the item's OWN
 *    case/tray box. Pogger's `BaseCandidate.units_deep` is just
 *    `facings_deep(shelf_depth)` over whatever box it was handed, so it packs
 *    whole cases for a CASE item and whole TRAYS for a TRAY item. Pass "CASE"
 *    and "TRAY". The `merchandising_style: UNIT` on the wire is not what is
 *    being counted, and reading it as such is the silent N x under-report.
 *  - NATIVE tray style: `TrayCandidate.units_deep` is
 *    `trays_deep * no_of_units_in_tray`, so the counted thing is back to a
 *    consumer unit. Pass "UNIT".
 *
 * Cited by symbol on purpose — the line numbers this doc used to carry had
 * already gone stale in both repos. The per-call-site mapping lives in
 * `owa-backend/src/services/merchflow/helpers/candidateWeight.ts`.
 *
 * Returns null when the weight is unknown, and ALSO when a CASE/TRAY count is
 * asked for without a usable pack size — the converter's `||` chains fall
 * through to the unscaled value there, which under-reports silently.
 */
export const weightPerCountedUnit = (
  unitWeightPounds: number | null,
  countedUnitStyle: CountedUnitStyle,
  unitsInCase: number | null | undefined,
  unitsInTray: number | null | undefined,
): number | null => {
  if (unitWeightPounds === null) {
    return null;
  }

  if (countedUnitStyle === "UNIT") {
    return unitWeightPounds;
  }

  const unitsPerCountedUnit = countedUnitStyle === "CASE" ? unitsInCase : unitsInTray;

  if (
    unitsPerCountedUnit === null ||
    unitsPerCountedUnit === undefined ||
    !Number.isFinite(unitsPerCountedUnit) ||
    unitsPerCountedUnit <= 0
  ) {
    return null;
  }

  return +(unitWeightPounds * unitsPerCountedUnit).toFixed(2);
};

/* ------------------------------------------------------------------------ *
 * REAL WEIGHT FIRST — the ladder above the derived formula.
 *
 * Everything above this line answers "what would this pack weigh, judging by
 * its name and size?". Everything below answers the prior question: "does a
 * data source already STATE what it weighs?" — because a measured weight
 * beats a good guess, and the derivation exists only to fill the gap
 * (reviewer objection on omni-common #581: deriving from pack size is a
 * workaround for missing data). Populate the master and the derivation goes
 * dormant instead of staying wrong.
 *
 * THE HAZARD THIS IS DESIGNED AGAINST. There is no real weight data yet, and
 * the placeholder is not null. Measured on merchant_db_45 (2026-08-26):
 *
 *     psa_product.weight : 19,755 rows, ALL = 1, uom ALL NULL, 1 distinct
 *     product_fact       : 0 rows
 *
 * A naive `if (weight) use it` therefore makes every product weigh one pound
 * — a 150 lb shelf reads as needing 150 units, and it looks plausible on
 * screen. That is worse than deriving, and it is the failure the two gates
 * below exist for:
 *
 *   GATE 1 (per row) — a weight without a unit is not a weight. `uom` is
 *   mandatory and must name a unit of MASS. Volume ("fl oz", "ml") and count
 *   ("ct", "ea") are not weights however plausible the number looks. Today
 *   every row has uom = NULL, so this one gate rejects the entire placeholder
 *   set on its own.
 *
 *   GATE 2 (per set) — a bare number is still not trustworthy. If a whole
 *   assortment reports the SAME weight, that is a default someone stamped on
 *   the column, not a measurement: real packs differ. `isDegenerateWeightSet`
 *   catches the next version of this bug, the one where somebody backfills
 *   `uom = 'LB'` across the table and leaves `weight = 1`.
 *
 * UNKNOWN IS NEVER ZERO still holds, now over three rungs: measured, else
 * derived, else unknown. A shelf of unweighable packs must not read as empty.
 * ------------------------------------------------------------------------ */

/** A weight as a data source carries it: a number AND the unit it is in. */
export interface StoredWeight {
  unitWeight?: number | null;
  weightUom?: string | null;
}

/** Where a weight came from. A measurement and a guess must not look alike. */
export type WeightProvenance = "measured" | "derived";

export interface ResolvedWeight {
  pounds: number;
  provenance: WeightProvenance;
}

/**
 * Pounds per unit, for units of MASS only.
 *
 * Anything absent from this table is rejected, which is deliberate: it makes
 * the safe answer the default one. "fl oz" is a volume and normalises to a
 * key that is not here, so it can never be mistaken for the "oz" beside it —
 * the exact disambiguation the reviewer named `uom` for.
 */
const POUNDS_PER_UOM: Readonly<Record<string, number>> = {
  lb: 1,
  lbs: 1,
  pound: 1,
  pounds: 1,
  oz: 1 / 16,
  ozs: 1 / 16,
  ounce: 1 / 16,
  ounces: 1 / 16,
  g: 0.00220462,
  gm: 0.00220462,
  gr: 0.00220462,
  gram: 0.00220462,
  grams: 0.00220462,
  kg: 2.20462,
  kgs: 2.20462,
  kilogram: 2.20462,
  kilograms: 2.20462,
};

/** Lowercase, drop punctuation, collapse whitespace: "FL. OZ." -> "fl oz". */
const normaliseUom = (uom: string): string =>
  uom
    .toLowerCase()
    .replace(/[.]/g, "")
    .replace(/\s+/g, " ")
    .trim();

/**
 * Pounds from a STATED weight, or null when the statement cannot be trusted
 * (gate 1). Null means "no measurement here" — the caller derives; it never
 * means zero. Keep full precision here; rounding belongs at an output boundary.
 */
export const measuredWeightPounds = (stored: StoredWeight | null | undefined): number | null => {
  if (!stored) {
    return null;
  }

  const amount = Number(stored.unitWeight);
  if (!Number.isFinite(amount) || amount <= 0) {
    return null;
  }

  // A weight without a unit is not a weight. This is the gate that rejects
  // every psa_product row we have: 19,755 of them, uom NULL on all.
  const uom = normaliseUom(String(stored.weightUom ?? ""));
  if (uom === "") {
    return null;
  }

  const poundsPerUnit = POUNDS_PER_UOM[uom];
  if (poundsPerUnit === undefined) {
    return null;
  }

  return amount * poundsPerUnit;
};

/**
 * The smallest set worth judging as a whole. Below this, "every row is the
 * same" is a coincidence — a two-product shelf of identical twin SKUs is
 * ordinary. At eight or more it is a stamped default.
 */
export const DEGENERATE_WEIGHT_SET_MIN = 8;

/**
 * Gate 2: does this whole set report one single weight? A real assortment
 * spans jugs, pods and powder boxes and cannot weigh the same to the gram, so
 * one distinct value across a category is a placeholder column, not data.
 *
 * Only rows that already passed gate 1 are considered — rows with no usable
 * stated weight neither prove nor disprove anything about the column.
 *
 * Returns false for a set too small to judge, so a caller that screens a
 * single product is not silently blinded.
 */
export const isDegenerateWeightSet = (
  products: readonly StoredWeight[] | null | undefined,
): boolean => {
  if (!products) {
    return false;
  }

  const seen = new Set<number>();

  for (const product of products) {
    const pounds = measuredWeightPounds(product);
    if (pounds !== null) {
      seen.add(pounds);
    }
  }

  // Counting DISTINCT values, not rows: it is the collapse to one value that
  // is the signal, and it needs enough rows behind it to mean something.
  if (seen.size !== 1) {
    return false;
  }

  let stated = 0;
  for (const product of products) {
    if (measuredWeightPounds(product) !== null) {
      stated += 1;
    }
  }

  return stated >= DEGENERATE_WEIGHT_SET_MIN;
};

export interface ResolveWeightOptions {
  /**
   * Set false when the surrounding set failed gate 2, to make every stated
   * weight in it fall through to the derived formula. Defaults to true: a
   * caller holding one product has no set to judge and gate 1 still applies.
   */
  trustStatedWeight?: boolean;
  /** Pack formats whose stated weights failed gate 2. */
  ignoredStyles?: ReadonlySet<CountedUnitStyle>;
}

/**
 * THE LADDER. Pounds for ONE CONSUMER UNIT, with where the number came from:
 *
 *   1. a stated weight that passes both gates      -> { provenance: "measured" }
 *   2. otherwise the derived laundry formula       -> { provenance: "derived" }
 *   3. otherwise null                              -> unknown, NEVER zero
 *
 * This is not the weight of a case or a tray — scale with `weightPerCountedUnit`.
 */
export const resolveUnitWeightPounds = (
  product: WeighableProduct & StoredWeight,
  options: ResolveWeightOptions = {},
): ResolvedWeight | null => {
  if (options.trustStatedWeight !== false) {
    const measured = measuredWeightPounds(product);
    if (measured !== null) {
      return { pounds: measured, provenance: "measured" };
    }
  }

  const derived = productWeightPounds(product);
  return derived === null ? null : { pounds: derived, provenance: "derived" };
};

/**
 * The ladder as a bare number, for callers that only want the pounds.
 *
 * NOTE the asymmetry with `isBulkProduct`, which reads the derived formula
 * only: bulk is a property of the pack a planogrammer merchandises against,
 * and it keeps answering the same way whether or not a master row exists.
 */
export const unitWeightPounds = (
  product: WeighableProduct & StoredWeight,
  options: ResolveWeightOptions = {},
): number | null => resolveUnitWeightPounds(product, options)?.pounds ?? null;

/** Develop's per-format stored weights. `weight` is still the primary stated value. */
export interface StoredWeights extends StoredWeight {
  trayWeight?: number | null;
  caseWeight?: number | null;
}

export interface PackCounts {
  noOfUnitsInTray?: number | null;
  noOfUnitsInCase?: number | null;
}

const asUnitStored = (
  product: WeighableProduct & StoredWeights,
): WeighableProduct & StoredWeight => ({
  name: product.name,
  size: product.size,
  unitWeight: product.unitWeight,
  weightUom: product.weightUom,
});

const storedAmountFor = (
  product: StoredWeights,
  style: CountedUnitStyle,
): number | null | undefined =>
  style === "CASE" ? product.caseWeight : style === "TRAY" ? product.trayWeight : product.unitWeight;

export const statedWeightPoundsFor = (
  stored: StoredWeights | null | undefined,
  style: CountedUnitStyle,
): number | null => {
  if (!stored) return null;
  return measuredWeightPounds({
    unitWeight: storedAmountFor(stored, style),
    weightUom: stored.weightUom,
  });
};

export const degenerateWeightStyles = (
  products: readonly StoredWeights[],
): Set<CountedUnitStyle> => {
  const styles = new Set<CountedUnitStyle>();
  for (const style of ["UNIT", "TRAY", "CASE"] as const) {
    if (
      isDegenerateWeightSet(
        products.map((product) => ({
          unitWeight: storedAmountFor(product, style),
          weightUom: product.weightUom,
        })),
      )
    ) {
      styles.add(style);
    }
  }
  return styles;
};

const unitsPerPack = (counts: PackCounts, style: CountedUnitStyle): number | null => {
  if (style === "UNIT") return 1;
  const count = Number(style === "CASE" ? counts.noOfUnitsInCase : counts.noOfUnitsInTray);
  return Number.isFinite(count) && count > 0 ? count : null;
};

/** A stated CASE/TRAY can still answer UNIT: divide by the pack count. */
const rescaledStatedPounds = (
  stored: StoredWeights,
  counts: PackCounts,
  target: CountedUnitStyle,
  ignoredStyles: ReadonlySet<CountedUnitStyle>,
): number | null => {
  const targetUnits = unitsPerPack(counts, target);
  if (targetUnits === null) return null;

  for (const source of ["UNIT", "TRAY", "CASE"] as const) {
    if (source === target || ignoredStyles.has(source)) continue;
    const sourcePounds = statedWeightPoundsFor(stored, source);
    if (sourcePounds === null) continue;
    const sourceUnits = unitsPerPack(counts, source);
    if (sourceUnits === null) continue;
    return (sourcePounds / sourceUnits) * targetUnits;
  }

  return null;
};

export const resolveCountedWeightPounds = (
  product: WeighableProduct & StoredWeights & PackCounts,
  countedUnitStyle: CountedUnitStyle,
  options: ResolveWeightOptions = {},
): ResolvedWeight | null => {
  const ignoredStyles = options.ignoredStyles ?? new Set<CountedUnitStyle>();

  if (options.trustStatedWeight !== false) {
    if (!ignoredStyles.has(countedUnitStyle)) {
      const exact = statedWeightPoundsFor(product, countedUnitStyle);
      if (exact !== null) {
        return { pounds: exact, provenance: "measured" };
      }
    }

    const rescaled = rescaledStatedPounds(product, product, countedUnitStyle, ignoredStyles);
    if (rescaled !== null) {
      return { pounds: rescaled, provenance: "measured" };
    }
  }

  const resolved = resolveUnitWeightPounds(asUnitStored(product), { trustStatedWeight: false });
  if (resolved === null) return null;

  const pounds = weightPerCountedUnit(
    resolved.pounds,
    countedUnitStyle,
    product.noOfUnitsInCase,
    product.noOfUnitsInTray,
  );
  return pounds === null ? null : { pounds, provenance: resolved.provenance };
};

export const countedWeightPounds = (
  product: WeighableProduct & StoredWeights & PackCounts,
  countedUnitStyle: CountedUnitStyle,
  options: ResolveWeightOptions = {},
): number | null => resolveCountedWeightPounds(product, countedUnitStyle, options)?.pounds ?? null;
